import React, { useState } from "react";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";

const quillModules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { header: [1, 2, 3, false] }],
    [{ font: [] }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"],
  ],
};

// Function to generate a URL-friendly link from the title
const formatLink = (title) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

export default function CreatePostForm({
  post,
  onSubmit,
  onCancel,
  previewMode,
  setPreviewMode,
}) {
  const [newPost, setNewPost] = useState({
    title: post?.title || "",
    content: post?.content || "",
    excerpt: post?.excerpt || "",
    author: post?.author || "",
    featuredImage: post?.featuredImage || "",
    readMins: post?.readMins || 0,
    category: post?.category || "Latest",
    status: post?.status || "draft",
    link: post?.link || "",
    keywords: post?.keywords || "",
    description: post?.description || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image Upload Function
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      console.log("Uploaded Image Response:", data); // Debugging log

      if (data.fileKey) {
        const imageUrl = `/` + data.fileKey; // Construct the full URL
        console.log("Final Image URL:", imageUrl); // Debugging log

        setNewPost((prev) => ({ ...prev, featuredImage: imageUrl }));
      } else {
        alert("Image upload failed: No fileKey returned from server.");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      alert("Failed to upload image");
    }
  };

  const handleCategoryChange = (e) => {
    setNewPost({ ...newPost, category: e.target.value });
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    if (
      !newPost.title ||
      !newPost.content ||
      !newPost.category ||
      !newPost.readMins ||
      !newPost.author ||
      !newPost.excerpt
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);
    const formattedLink = formatLink(newPost.title);

    const postToSubmit = {
      ...newPost,
      link: formattedLink,
      featuredImage: newPost.featuredImage, // Ensure the image is included
      keywords: newPost.keywords,         // Add this line
      description: newPost.description    // Add this line
    };

    alert(
      post?._id ? "Post updated successfully" : "Post created successfully"
    );
    console.log("Final post object:", postToSubmit); // Debugging log
    onSubmit(postToSubmit);
    setIsSubmitting(false);
  };

  const handleDelete = () => {
    if (!post?._id) return;

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this post?"
    );
    if (!confirmDelete) return;

    alert("Post deleted successfully");
    onCancel();
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg shadow-lg mb-6">
      <h3 className="text-xl font-semibold mb-4">
        {post?._id ? "Edit Post" : "Create Post"}
      </h3>
      <input
        type="text"
        placeholder="Title"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.title}
        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
      />
      <input
        type="text"
        placeholder="Keywords (comma separated)"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.keywords || ""}
        onChange={(e) => setNewPost({ ...newPost, keywords: e.target.value })}
      />

      <textarea
        placeholder="Meta Description"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.description || ""}
        onChange={(e) =>
          setNewPost({ ...newPost, description: e.target.value })
        }
      />
      <textarea
        placeholder="Excerpt"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.excerpt}
        onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
      />
      <input
        type="text"
        placeholder="Author"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.author}
        onChange={(e) => setNewPost({ ...newPost, author: e.target.value })}
      />

      <label className="block mb-4">
        <span>Featured Image</span>
        <input
          type="file"
          accept="image/*"
          className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
          onChange={(e) => uploadImage(e.target.files[0])}
        />
        {newPost.featuredImage && (
          <img
            src={newPost.featuredImage}
            alt="Featured"
            className="mt-4 max-w-full h-auto"
          />
        )}
      </label>

      <input
        type="number"
        placeholder="Estimated Reading Time (Minutes)"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.readMins}
        onChange={(e) => setNewPost({ ...newPost, readMins: e.target.value })}
      />

      <label className="block mb-4">
        <span>Category</span>
        <select
          className="form-select mt-1 block w-full rounded-lg border border-gray-300"
          value={newPost.category}
          onChange={handleCategoryChange}
        >
          <option value="Technology">Technology</option>
          <option value="Health">Health</option>
          <option value="Education">Education</option>
          <option value="Latest">Latest</option>
          <option value="Featured">Featured</option>
          <option value="News">News</option>
        </select>
      </label>

      <input
        type="text"
        placeholder="Link (URL)"
        className="w-full p-3 mb-4 rounded-lg border border-gray-300 shadow-sm"
        value={newPost.link}
        onChange={(e) => setNewPost({ ...newPost, link: e.target.value })}
        onBlur={(e) =>
          setNewPost({ ...newPost, link: formatLink(e.target.value) })
        }
      />

      <ReactQuill
        value={newPost.content}
        onChange={(value) => setNewPost({ ...newPost, content: value })}
        modules={quillModules}
        className="mb-4"
      />

      <label className="block mb-4">
        <span>Status</span>
        <select
          className="form-select mt-1 block w-full rounded-lg border border-gray-300"
          value={newPost.status}
          onChange={(e) => setNewPost({ ...newPost, status: e.target.value })}
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </label>

      <div className="flex gap-4">
        {!post?._id && (
          <button
            className="bg-green-500 text-white px-4 py-2 rounded-lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Create Post
          </button>
        )}
        {post?._id && (
          <button
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            Update Post
          </button>
        )}
        <button
          className="bg-gray-300 text-black px-4 py-2 rounded-lg"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>

      {post?._id && (
        <button
          className="bg-red-500 text-white px-4 py-2 rounded-lg mt-4"
          onClick={handleDelete}
        >
          Delete Post
        </button>
      )}
    </div>
  );
}
